import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { getAuthHeaders } from '@/identity.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import type {
  Automation,
  AutomationSchedule,
  ScheduleRule,
} from '@/integration/types.js';
import { notify } from '@/lib/notify.js';
import { apiFetch } from '@/lib/api-fetch.js';

/** Body for creating an automation. `orgId` is injected server-side by the BFF, which also
 *  generates the `name` from the prompt — the UI omits it, but a direct API caller may still send one. */
export interface AutomationCreateInput {
  name?: string;
  capability: string;
  schedule: AutomationSchedule;
  payload: { prompt: string } & Record<string, unknown>;
  /** Maximum number of scheduler-claimed ticks; omit = unlimited. (No `null` on create —
   *  use PATCH `maxFires: null` to reset an existing limit.) */
  maxFires?: number;
  /** The first run of a `planned` schedule, as Interpret previewed it; omit to let the planner decide. */
  plan?: AutomationPlanInput;
}

/** A next-run decision sent with a `planned` schedule: the instant and the one-line why. */
export interface AutomationPlanInput {
  at: string;
  reason: string;
}

/**
 * Partial patch for an automation. `payload` must be sent whole when changed
 * (the scheduler treats it opaquely) — callers preserve the existing payload
 * and override only the fields they edit.
 */
export interface AutomationPatch {
  name?: string;
  schedule?: AutomationSchedule;
  payload?: { prompt?: string } & Record<string, unknown>;
  /** Maximum number of scheduler-claimed ticks; null = reset to unlimited. */
  maxFires?: number | null;
  /** The first run of a `planned` schedule; omit to let the planner decide. */
  plan?: AutomationPlanInput;
}

export interface AutomationProjectionPatch {
  name?: string;
  schedule?: AutomationSchedule;
  contentText?: string;
  maxFires?: number | null;
  /** The user's own words for a custom schedule; null clears them when a preset replaces it. */
  scheduleText?: string | null;
  runOnHolidays?: boolean;
  /** The first run of a `planned` schedule; omit to let the planner decide. */
  plan?: AutomationPlanInput;
}

/** A schedule the interpreter compiled to a cron. Older BFFs send no `kind`. */
export interface ParsedCronSchedule {
  kind?: 'cron';
  cron: string;
  tz: string;
  summary: string;
  /** ISO timestamps of the next runs, soonest first. */
  nextFires: string[];
}

/** A schedule with no cron: the planner decides each next run from the words. */
export interface ParsedPlannedSchedule {
  kind: 'planned';
  tz: string;
  summary: string;
  /** True when the dates depend on what each run finds; the preview then holds the first run only. */
  runDecides: boolean;
  /** ISO timestamps of the planner's first runs, soonest first; empty when the planner did not answer. */
  nextFires: string[];
  /** The planner's one-line why for the first run, or null when it did not answer. */
  reason: string | null;
  /** The rule the runs are computed from; absent when a model picks the dates. */
  rule?: ScheduleRule;
}

/** What `POST /v1/automations/parse-schedule` makes of the user's words. */
export type ParsedSchedule =
  ParsedCronSchedule | ParsedPlannedSchedule | { unsupported: string };

/** Where a `planned` automation's next run stands. */
export type AutomationPlanStatus = 'planned' | 'planning' | 'stuck';

/** Who made the latest next-run decision. */
export type AutomationPlanSource =
  'creation' | 'run' | 'planner' | 'user' | 'rule';

/** The automation whose run created this one. */
export interface AutomationParentRef {
  id: string;
  /** Null when the parent has no name. */
  name: string | null;
}

/**
 * The last time a due run was skipped instead of fired. `reason` is kept as sent: this build names
 * `child_daily_budget` and shows any other reason generically.
 */
export interface AutomationSkip {
  reason: string;
  at: string;
}

/** The plan of a `planned` automation, as the projection reports it; null for every other schedule. */
export interface AutomationPlanState {
  status: AutomationPlanStatus;
  /** Null when the server names a source this build does not know. */
  source: AutomationPlanSource | null;
  reason: string | null;
}

/** Shown when the edge predates the schedule interpreter (an unrouted or non-JSON answer). */
export const CUSTOM_SCHEDULE_UNAVAILABLE =
  'Custom schedules are not available yet.';

const ENDPOINT = '/v1/automations';
const PROJECTION_ENDPOINT = '/v2/automations';

export type AutomationExecutionStatus =
  'scheduled' | 'running' | 'queued' | 'sent' | 'delivery_unknown' | 'failed';

export type AutomationQueuedReason =
  'quiet_hours' | 'teams_outage' | 'rate_limited' | 'retry_backoff';

export interface AutomationExecutionProjection {
  status: AutomationExecutionStatus;
  queuedReason: AutomationQueuedReason | null;
  scheduledFor: string;
  acceptedAt: string | null;
  sentAt: string | null;
  failureCode: string | null;
}

/** Who else can see a scheduled automation: only its author, a chosen group, or the whole workspace. */
export type AutomationVisibility = 'private' | 'group' | 'workspace';

/** The automation's author, as the detail projection names them. */
export interface AutomationOwner {
  userId: string;
  displayName: string | null;
}
export type AutomationMessageRoute =
  | { source: 'skill' }
  | {
      source: 'user';
      provider: 'teams';
      connection_id: string;
      team_id?: string;
      channel_id?: string;
      chat_id?: string;
    }
  | {
      source: 'user';
      provider: 'secretary';
      recipient: { aad_id?: string; email?: string; name?: string };
    };

interface AutomationProjectionBase {
  id: string;
  name: string | null;
  capability: string;
  schedule: AutomationSchedule;
  enabled: boolean;
  pausedAt: string | null;
  nextFireAt: string | null;
  pauseReason: string | null;
  maxFires: number | null;
  fireCount: number;
  latestExecution: AutomationExecutionProjection | null;
  /** Ids of the skills attached to this automation's payload, in attach order. */
  skillIds: string[];
  /** The user's own words for a custom schedule (`payload.schedule_text`); null for a preset. */
  scheduleText: string | null;
  /** The next-run plan of a `planned` automation; null for cron and manual rows and on older servers. */
  plan: AutomationPlanState | null;
  /** The automation whose run created this one; null for one a person made, and on older servers. */
  parent: AutomationParentRef | null;
  /** 0 for an automation a person made, 1 for one a run created; 0 on older servers. */
  depth: number;
  /** The last skipped run; null when none was skipped, and on older servers. */
  skip: AutomationSkip | null;
  runOnHolidays: boolean;
  pendingDeferral: {
    until: string;
    reason: 'public_holiday';
    country: string | null;
  } | null;
  visibility: AutomationVisibility;
  /** Whether the caller may change this automation's audience, schedule or content — not just view it. */
  canManage: boolean;
  /** User ids the automation is shared with when `visibility` is `group`; empty otherwise. */
  participantIds: string[];
  /**
   * Id of the automation's dedicated chat, or `null` when it posts into the author's regular chat
   * (an agent-created automation with no chat of its own). A reader's row and the chat header pill
   * both key off this — no dedicated chat means nothing to open or to share workspace-wide.
   */
  conversationId: string | null;
  messageRoute?: AutomationMessageRoute;
}

export interface AutomationSummaryProjection extends AutomationProjectionBase {
  recipientDisplayName: string | null;
  preview: string;
}

export interface AutomationDetailProjection extends AutomationProjectionBase {
  recipient: { displayName: string; email: string | null } | null;
  content: {
    kind: 'message' | 'task' | 'checkin' | 'legacy';
    text: string;
    sourceRequest?: string;
  };
  owner: AutomationOwner | null;
}

/** One entry of an automation's run history, as the results feed returns it. */
export interface AutomationResult {
  id: string;
  createdAt: string;
  text: string;
  attachmentCount: number;
  /** The insights run behind this result's report, or null when it carries none. */
  reportRunId: string | null;
}

/** A page of an automation's run history, newest first; `beforeId` pages further into the past. */
export interface AutomationResultsPage {
  items: AutomationResult[];
  lastRunAt: string | null;
}

export interface FireNowResult {
  fired: boolean;
  fireId: string;
  status?: number;
  reason?: string;
}

/**
 * Platform-JWT bearer (shared) + JSON content type for the scheduler CRUD.
 * conversation-service is the BFF that forwards to cti-scheduler; the browser
 * authenticates only with the platform JWT (zero-trust — no x-user-id headers).
 */
async function authHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

/** Pull a human-readable error message out of a non-ok response body. */
async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: string };
    if (body.message) return body.message;
  } catch {
    if (text.trim()) return text.trim();
  }
  return `${fallback} (${res.status})`;
}

/**
 * True when the response cannot have come from the projection API. `/v2` is a
 * shadow surface the edge does not route yet: an unrouted GET falls through to
 * the SPA handler and comes back as the app shell — HTTP 200 `text/html`,
 * never a 404 — and an unrouted write gets nginx's own non-JSON error page. So
 * "projection unavailable" is any non-JSON answer; 404 is kept for a routed
 * BFF that predates the projection controller. A JSON error (a real projection
 * failure) stays on the error path and is never silently retried on `/v1`.
 */
function projectionUnavailable(res: Response): boolean {
  if (res.status === 404) return true;
  const contentType = res.headers.get('content-type') ?? '';
  return !contentType.toLowerCase().includes('application/json');
}

/** The list URL, scoped to a workspace when one is given (snake_case `workspace_id` on the wire). */
function automationsUrl(workspaceId?: string | null): string {
  if (!workspaceId) return ENDPOINT;
  return `${ENDPOINT}?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`;
}

function projectionUrl(workspaceId?: string | null): string {
  if (!workspaceId) return PROJECTION_ENDPOINT;
  return `${PROJECTION_ENDPOINT}?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`;
}

async function fetchAutomations(
  workspaceId?: string | null,
): Promise<Automation[]> {
  const headers = await authHeaders();
  const res = await apiFetch(automationsUrl(workspaceId), { headers });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't load scheduled runs");
    notify.error(message);
    throw new Error(message);
  }
  // The scheduler list endpoint returns a paginated envelope
  // `{ items, total }` (forwarded 1:1 by the BFF), not a bare array. Unwrap
  // `items`; tolerate a bare array too in case the contract ever changes.
  const body = (await res.json()) as Automation[] | { items?: Automation[] };
  return Array.isArray(body) ? body : (body.items ?? []);
}

/**
 * Single automation. Returns `null` on 404 so the `/scheduled/$id` route can
 * render a not-found state instead of a toast (a missing/deleted automation is
 * an expected navigation outcome, not an error). Other non-ok statuses notify
 * and throw.
 */
async function fetchAutomation(id: string): Promise<Automation | null> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, { headers });
  if (res.status === 404) return null;
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't load the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
  return (await res.json()) as Automation;
}

async function fetchAutomationSummaries(
  workspaceId?: string | null,
): Promise<AutomationSummaryProjection[]> {
  const headers = await authHeaders();
  const res = await apiFetch(projectionUrl(workspaceId), { headers });
  if (projectionUnavailable(res)) {
    return (await fetchAutomations(workspaceId)).map(projectLegacySummary);
  }
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't load scheduled runs");
    notify.error(message);
    throw new Error(message);
  }
  const body = (await res.json()) as
    AutomationSummaryProjection[] | { items?: AutomationSummaryProjection[] };
  const items = Array.isArray(body) ? body : (body.items ?? []);
  return items.map((item) =>
    normalizeAudience({
      ...item,
      skillIds: normalizeSkillIds(item.skillIds),
      scheduleText: normalizeScheduleText(item.scheduleText),
      plan: normalizePlan(item.plan),
      ...normalizeLineage(item),
      runOnHolidays: item.runOnHolidays === true,
      pendingDeferral: item.pendingDeferral ?? null,
      conversationId: normalizeConversationId(item),
      messageRoute: parseMessageRoute(item.messageRoute),
    }),
  );
}

/**
 * The projection's dedicated-chat id. Like every other field on this projection
 * (`canManage`, `participantIds`, `nextFireAt`, …) it lands camelCase on the wire — this is a BFF
 * projection, not a raw service DTO. Absent (a server that predates it) normalizes to `null`, same
 * posture as the audience fields below.
 */
function normalizeConversationId(item: unknown): string | null {
  const conversationId = (item as { conversationId?: unknown }).conversationId;
  return typeof conversationId === 'string' && conversationId.length > 0
    ? conversationId
    : null;
}

/**
 * The projection's `skillIds`, made to match the `string[]` its type claims. The response is cast,
 * not validated: a server that predates the field sends nothing, and a legacy or broken one could
 * send a non-string or a blank id, which would then flow into React keys, chip counts and skill
 * lookups. Same strictness as `extractSkillIds` on the v1 paths, so all four read paths agree.
 */
function normalizeSkillIds(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is string =>
          typeof entry === 'string' && entry.trim().length > 0,
      )
    : [];
}

/**
 * The projection's `scheduleText`, made to match the `string | null` its type claims: a server that
 * predates the field sends nothing, and blank words are no words. Same strictness as
 * `extractScheduleText` on the v1 paths.
 */
function normalizeScheduleText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

const PLAN_STATUSES: ReadonlySet<string> = new Set<AutomationPlanStatus>([
  'planned',
  'planning',
  'stuck',
]);

const PLAN_SOURCES: ReadonlySet<string> = new Set<AutomationPlanSource>([
  'creation',
  'run',
  'planner',
  'user',
  'rule',
]);

/**
 * The projection's `plan`, made to match the `AutomationPlanState | null` its type claims. A server
 * that predates planned schedules sends nothing; an unknown status is no plan at all, while an
 * unknown source or a blank reason only drops that one field.
 */
function normalizePlan(value: unknown): AutomationPlanState | null {
  if (!value || typeof value !== 'object') return null;
  const { status, source, reason } = value as Record<string, unknown>;
  if (typeof status !== 'string' || !PLAN_STATUSES.has(status)) return null;
  return {
    status: status as AutomationPlanStatus,
    source:
      typeof source === 'string' && PLAN_SOURCES.has(source)
        ? (source as AutomationPlanSource)
        : null,
    reason: normalizeScheduleText(reason),
  };
}

/**
 * The projection's lineage (`parent`, `depth`, `skip`), made to match its type. A server that
 * predates automations created by automations sends none of them: that reads as an automation a
 * person made, with nothing skipped.
 */
function normalizeLineage(
  item: unknown,
): Pick<AutomationProjectionBase, 'parent' | 'depth' | 'skip'> {
  const { parent, depth, skip } = item as Record<string, unknown>;
  return {
    parent: normalizeParent(parent),
    depth:
      Number.isInteger(depth) && (depth as number) >= 0 ? (depth as number) : 0,
    skip: normalizeSkip(skip),
  };
}

function normalizeParent(value: unknown): AutomationParentRef | null {
  if (!value || typeof value !== 'object') return null;
  const { id, name } = value as Record<string, unknown>;
  if (typeof id !== 'string' || !id.trim()) return null;
  return { id, name: normalizeScheduleText(name) };
}

function normalizeSkip(value: unknown): AutomationSkip | null {
  if (!value || typeof value !== 'object') return null;
  const { reason, at } = value as Record<string, unknown>;
  if (typeof reason !== 'string' || !reason.trim()) return null;
  if (typeof at !== 'string' || !Number.isFinite(Date.parse(at))) return null;
  return { reason, at };
}

/**
 * The projection's audience fields (`visibility`, `canManage`, `participantIds`), defaulted for a
 * server that predates the access model: private, manageable by the caller, nobody added. Same
 * posture as `normalizeSkillIds` — the response is cast, not validated, so a server that sends
 * nothing for these fields must not leave the projection lying about its own type.
 */
function normalizeAudience<
  T extends {
    visibility?: AutomationVisibility;
    canManage?: boolean;
    participantIds?: string[];
  },
>(
  item: T,
): T & {
  visibility: AutomationVisibility;
  canManage: boolean;
  participantIds: string[];
} {
  return {
    ...item,
    visibility: item.visibility ?? 'private',
    canManage: item.canManage ?? true,
    participantIds: Array.isArray(item.participantIds)
      ? item.participantIds
      : [],
  };
}

async function fetchAutomationProjection(
  id: string,
): Promise<AutomationDetailProjection | null> {
  const headers = await authHeaders();
  const res = await fetch(`${PROJECTION_ENDPOINT}/${encodeURIComponent(id)}`, {
    headers,
  });
  if (projectionUnavailable(res)) {
    const legacy = await fetchAutomation(id);
    return legacy ? projectLegacyDetail(legacy) : null;
  }
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't load the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
  const projection = (await res.json()) as AutomationDetailProjection;
  return normalizeAudience({
    ...projection,
    skillIds: normalizeSkillIds(projection.skillIds),
    scheduleText: normalizeScheduleText(projection.scheduleText),
    plan: normalizePlan(projection.plan),
    ...normalizeLineage(projection),
    runOnHolidays: projection.runOnHolidays === true,
    pendingDeferral: projection.pendingDeferral ?? null,
    owner: projection.owner ?? null,
    conversationId: normalizeConversationId(projection),
    messageRoute: parseMessageRoute(projection.messageRoute),
  });
}

export async function createAutomation(
  input: AutomationCreateInput,
  workspaceId?: string | null,
): Promise<Automation> {
  const headers = await authHeaders();
  // Stamp the selected workspace (snake_case wire) so the BFF authors the automation's dedicated chat
  // under it (global-workspaces 2.5a). Omitted when none is selected — the BFF then falls back to the
  // caller's personal workspace.
  const body = workspaceId ? { ...input, workspace_id: workspaceId } : input;
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await errorMessage(
      res,
      "Couldn't create the scheduled run",
    );
    notify.error(message);
    throw new Error(message);
  }
  return (await res.json()) as Automation;
}

/**
 * Interpret words through the platform. An unrouted endpoint returns an unsupported result;
 * transport failures are handled by the picker. The body is snake_case on the wire.
 */
export async function parseSchedule(
  text: string,
  tz: string,
): Promise<ParsedSchedule> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/parse-schedule`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, tz }),
  });
  const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
  if (
    res.status === 404 ||
    res.status === 405 ||
    (res.ok && contentType.includes('text/html'))
  ) {
    return { unsupported: CUSTOM_SCHEDULE_UNAVAILABLE };
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, "Couldn't interpret the schedule"));
  }
  return interpretationFromBody(await res.json());
}

const INVALID_INTERPRETATION = 'Invalid schedule interpretation response';

/** The interpreter's JSON answer, checked strictly: anything off-contract throws. */
function interpretationFromBody(body: unknown): ParsedSchedule {
  if (!body || typeof body !== 'object' || Array.isArray(body))
    throw new Error(INVALID_INTERPRETATION);
  const fields = body as Record<string, unknown>;
  if ('unsupported' in fields) {
    return { unsupported: requiredText(fields.unsupported) };
  }
  if (fields.kind === 'planned') return plannedFromFields(fields);
  if (fields.kind !== undefined && fields.kind !== 'cron')
    throw new Error(INVALID_INTERPRETATION);
  return {
    cron: requiredText(fields.cron),
    tz: requiredTimezone(fields.tz),
    summary: requiredText(fields.summary),
    nextFires: previewInstants('next_fires' in fields ? fields.next_fires : []),
  };
}

/** A planned answer: no cron, at most three preview runs, the planner's reason or null, and any rule. */
function plannedFromFields(
  fields: Record<string, unknown>,
): ParsedPlannedSchedule {
  const nextFires = previewInstants(fields.next_fires);
  if (typeof fields.run_decides !== 'boolean' || nextFires.length > 3)
    throw new Error(INVALID_INTERPRETATION);
  const rule = optionalRule(fields.rule);
  return {
    kind: 'planned',
    tz: requiredTimezone(fields.tz),
    summary: requiredText(fields.summary),
    runDecides: fields.run_decides,
    nextFires,
    reason: fields.reason === null ? null : requiredText(fields.reason),
    ...(rule ? { rule } : {}),
  };
}

/** The answer's rule as sent, null when there is none; anything but an object throws. */
function optionalRule(value: unknown): ScheduleRule | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'object' || Array.isArray(value))
    throw new Error(INVALID_INTERPRETATION);
  return value as ScheduleRule;
}

/** A non-blank string, as sent. */
function requiredText(value: unknown): string {
  if (typeof value !== 'string' || !value.trim())
    throw new Error(INVALID_INTERPRETATION);
  return value;
}

/** A non-blank IANA zone this browser can format in. */
function requiredTimezone(value: unknown): string {
  const timezone = requiredText(value);
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone });
  } catch {
    throw new Error(INVALID_INTERPRETATION);
  }
  return timezone;
}

/** ISO-8601 instants with an explicit offset; anything else throws. */
function previewInstants(value: unknown): string[] {
  if (
    !Array.isArray(value) ||
    !value.every(
      (instant): instant is string =>
        typeof instant === 'string' &&
        /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(instant) &&
        Number.isFinite(Date.parse(instant)),
    )
  )
    throw new Error(INVALID_INTERPRETATION);
  return value;
}

async function updateAutomation(args: {
  id: string;
  patch: AutomationPatch;
}): Promise<Automation> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(args.id)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(args.patch),
  });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't save the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
  return (await res.json()) as Automation;
}

/**
 * Legacy save path for an edit made on a detail that itself fell back to
 * `/v1`: translate the safe-field patch onto the legacy PATCH shape. The
 * legacy contract wants `payload` sent whole, so the current automation is
 * read first and only the body text and the schedule words are overridden — a
 * check-in keeps its instruction in `payload.question`, everything else in
 * `payload.prompt`; the words live in `payload.schedule_text`.
 */
async function updateAutomationLegacy(args: {
  id: string;
  patch: AutomationProjectionPatch;
}): Promise<AutomationDetailProjection> {
  const { contentText, scheduleText, runOnHolidays, ...sharedFields } =
    args.patch;
  const patch: AutomationPatch = { ...sharedFields };
  if (
    contentText !== undefined ||
    scheduleText !== undefined ||
    runOnHolidays !== undefined
  ) {
    const automation = await fetchAutomation(args.id);
    if (!automation) {
      const message = "Couldn't save the scheduled run";
      notify.error(message);
      throw new Error(message);
    }
    patch.payload = legacyPayloadPatch(automation, contentText, scheduleText);
    if (runOnHolidays !== undefined)
      patch.payload.run_on_holidays = runOnHolidays;
    if (args.patch.schedule !== undefined && scheduleText === undefined) {
      delete patch.payload.schedule_text;
    }
  }
  return projectLegacyDetail(await updateAutomation({ id: args.id, patch }));
}

/** The whole payload with edits applied; null explicitly asks the scheduler to clear saved words. */
function legacyPayloadPatch(
  automation: Automation,
  contentText: string | undefined,
  scheduleText: string | null | undefined,
): NonNullable<AutomationPatch['payload']> {
  const payload =
    contentText === undefined
      ? { ...automation.payload }
      : payloadWithContent(automation, contentText);
  if (scheduleText !== undefined) payload.schedule_text = scheduleText;
  return payload;
}

/**
 * The payload with the body text written where cti-conversation-service's `patchContent` writes it:
 * `message.text`, `task.prompt` or `checkin.question` for a shaped payload, otherwise a top-level
 * `question` for a check-in (by capability or by an existing `question`) and `prompt` for the rest.
 */
function payloadWithContent(
  automation: Automation,
  contentText: string,
): Record<string, unknown> {
  const payload = automation.payload;
  const message = plainRecord(payload.message);
  if (message)
    return { ...payload, message: { ...message, text: contentText } };
  const task = plainRecord(payload.task);
  if (task) return { ...payload, task: { ...task, prompt: contentText } };
  const checkin = plainRecord(payload.checkin);
  if (checkin) {
    return { ...payload, checkin: { ...checkin, question: contentText } };
  }
  const isCheckin =
    automation.capability === CHECKIN_CAPABILITY ||
    typeof payload.question === 'string';
  return { ...payload, [isCheckin ? 'question' : 'prompt']: contentText };
}

async function updateAutomationProjection(args: {
  id: string;
  patch: AutomationProjectionPatch;
}): Promise<AutomationDetailProjection> {
  const headers = await authHeaders();
  const res = await fetch(
    `${PROJECTION_ENDPOINT}/${encodeURIComponent(args.id)}`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify(args.patch),
    },
  );
  /**
   * The write path additionally requires a FAILED response before falling
   * back: an unrouted PATCH comes back as the edge's non-JSON error (nginx
   * 405/404), while a hypothetical ok-but-non-JSON success must never be
   * retried through the legacy write — that would double-apply the edit.
   */
  if (!res.ok && projectionUnavailable(res)) {
    return updateAutomationLegacy(args);
  }
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't save the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
  return (await res.json()) as AutomationDetailProjection;
}

async function deleteAutomation(id: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok && res.status !== 204) {
    const message = await errorMessage(
      res,
      "Couldn't delete the scheduled run",
    );
    notify.error(message);
    throw new Error(message);
  }
}

async function pauseAutomation(id: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't pause the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
}

async function unpauseAutomation(id: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/unpause`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const message = await errorMessage(
      res,
      "Couldn't resume the scheduled run",
    );
    notify.error(message);
    throw new Error(message);
  }
}

/** Trigger an immediate fire of an automation now (delivers into its dedicated chat) instead of
 *  waiting for the schedule. Callers attach their own success toast; the shared hook stays
 *  callback-less so it can't double-toast a callsite that already passes `onSuccess`. */
async function fireNowAutomation(id: string): Promise<FireNowResult> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/fire-now`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't start the scheduled run");
    notify.error(message);
    throw new Error(message);
  }
  return (await res.json()) as FireNowResult;
}

/**
 * TanStack Query factory for the user's automations. Pass the selected workspace on the automations
 * SCREEN so the fetch is scoped to it server-side (`?workspace_id`) and switching workspace refetches
 * (key `['automations', ws]`); the sidebar/chat/command-palette dedicated-chat lookups call it with no
 * argument and keep the shared, unscoped `['automations']` list they need to subtract dedicated chats
 * across every workspace.
 */
export function automationsQueryOptions(workspaceId?: string | null) {
  return queryOptions({
    queryKey:
      workspaceId === undefined
        ? ['automations']
        : ['automations', workspaceId],
    queryFn: () => fetchAutomations(workspaceId),
    enabled: workspaceId !== null,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Single-automation lookup (BFF has a dedicated GET /:id). */
export function automationQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['automation', id],
    queryFn: async (): Promise<Automation | null> => {
      if (!id) return null;
      return fetchAutomation(id);
    },
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/** Privacy-safe list used only by the Scheduled screen. */
export function automationSummariesQueryOptions(workspaceId?: string | null) {
  return queryOptions({
    queryKey: ['automation-summaries', workspaceId],
    queryFn: () => fetchAutomationSummaries(workspaceId),
    enabled: workspaceId !== null,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Owner-scoped detail with exact content but no raw payload or target. */
export function automationProjectionQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['automation-projection', id],
    queryFn: async (): Promise<AutomationDetailProjection | null> => {
      if (!id) return null;
      return fetchAutomationProjection(id);
    },
    enabled: id !== undefined,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/**
 * Change who besides its author can see a scheduled automation. `participantIds` is sent only when
 * provided (undefined leaves the stored group untouched, e.g. a plain switch to `workspace`).
 */
export async function updateAutomationAudience(
  id: string,
  audience: { visibility: AutomationVisibility; participantIds?: string[] },
): Promise<{ visibility: AutomationVisibility; participantIds: string[] }> {
  const body: Record<string, unknown> = { visibility: audience.visibility };
  if (audience.participantIds !== undefined) {
    body.participant_ids = audience.participantIds;
  }
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/audience`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't update who can see this");
    notify.error(message);
    throw new Error(message);
  }
  const wire = (await res.json()) as {
    visibility: AutomationVisibility;
    participant_ids: string[];
  };
  return { visibility: wire.visibility, participantIds: wire.participant_ids };
}

interface AutomationResultWire {
  id: string;
  created_at: string;
  text: string;
  attachment_count: number;
  report_run_id?: string | null;
}

/** An automation's run history, newest first; `before_id` pages further into the past. */
export async function fetchAutomationResults(
  id: string,
  options?: { beforeId?: string; limit?: number },
): Promise<AutomationResultsPage> {
  const params = new URLSearchParams();
  if (options?.beforeId !== undefined)
    params.set('before_id', options.beforeId);
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  const query = params.toString();
  const url = `${PROJECTION_ENDPOINT}/${encodeURIComponent(id)}/results${
    query ? `?${query}` : ''
  }`;
  const headers = await authHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const message = await errorMessage(res, "Couldn't load the run history");
    notify.error(message);
    throw new Error(message);
  }
  const body = (await res.json()) as {
    items: AutomationResultWire[];
    last_run_at: string | null;
  };
  return {
    items: body.items.map((item) => ({
      id: item.id,
      createdAt: item.created_at,
      text: item.text,
      attachmentCount: item.attachment_count,
      reportRunId: item.report_run_id ?? null,
    })),
    lastRunAt: body.last_run_at,
  };
}

/** TanStack Query factory for an automation's run history. */
export function automationResultsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['automation-results', id],
    queryFn: async (): Promise<AutomationResultsPage> => {
      if (!id) return { items: [], lastRunAt: null };
      return fetchAutomationResults(id);
    },
    enabled: id !== undefined,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateAutomation(): UseMutationResult<
  Automation,
  Error,
  AutomationCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AutomationCreateInput) =>
      createAutomation(input, useUiStore.getState().selectedWorkspaceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
    },
  });
}

export function useUpdateAutomation(): UseMutationResult<
  Automation,
  Error,
  { id: string; patch: AutomationPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateAutomation,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation', vars.id] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({
        queryKey: ['automation-projection', vars.id],
      });
    },
  });
}

export function useUpdateAutomationProjection(): UseMutationResult<
  AutomationDetailProjection,
  Error,
  { id: string; patch: AutomationProjectionPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateAutomationProjection,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation', vars.id] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({
        queryKey: ['automation-projection', vars.id],
      });
    },
  });
}

/**
 * Deletes an automation and refreshes both lists it can empty. Deleting an
 * automation also soft-deletes its own dedicated chat, so the sidebar's chat
 * lists have to be refetched too — otherwise the deleted chat sits in Recents,
 * unread dot and all, until the page is reloaded, and opening it says the chat
 * is no longer available.
 */
export function useDeleteAutomation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteAutomation,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({ queryKey: ['recents'] });
      void qc.invalidateQueries({ queryKey: ['archived-conversations'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function usePauseAutomation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pauseAutomation,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation', id] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({ queryKey: ['automation-projection', id] });
    },
  });
}

export function useUnpauseAutomation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: unpauseAutomation,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation', id] });
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({ queryKey: ['automation-projection', id] });
    },
  });
}

export function useFireNowAutomation(): UseMutationResult<
  FireNowResult,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fireNowAutomation,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['automation-summaries'] });
      void qc.invalidateQueries({ queryKey: ['automation-projection', id] });
    },
  });
}

/**
 * Capability id of a one-time Secretary reminder. At fire time the Secretary
 * bot sends a 1:1 Teams ping instead of running an agent; the UI keys the
 * reminder-only affordances (recipient field, detail-page edit) off this id.
 */
export const REMINDER_CAPABILITY = 'secretary-reminder';

/**
 * Capability id of a Secretary check-in. Its instruction text lives in
 * `payload.question` (there is NO `payload.prompt`), and it carries a
 * `payload.recipient`; the UI keys the check-in body and recipient
 * affordances off this id, mirroring the reminder handling.
 */
export const CHECKIN_CAPABILITY = 'secretary-checkin';

/**
 * Mirror of the scheduler's fire gate (`isFireBlocked` in cti-scheduler):
 * Manual schedules are disabled by design but remain fireable. Agent-chat also permits
 * manual execution after its automatic budget is spent. Explicit user pauses still block.
 */
export function isFireBlockedAutomation(automation: Automation): boolean {
  if (
    automation.capability === 'agent-chat' &&
    automation.pauseReason === 'max_fires'
  )
    return false;
  if (automation.enabled) return false;
  const ticks =
    automation.schedule.type === 'planned' ||
    (automation.schedule.type !== 'manual' &&
      typeof automation.schedule.cron === 'string' &&
      automation.schedule.cron.length > 0);
  return ticks || automation.pausedAt != null;
}

/**
 * Whether a fired run delivers its result somewhere other than this chat, at a
 * time of its own choosing. An insights run posts nothing when it fires: the
 * report arrives later through the webhook, and the runner deliberately writes
 * no placeholder ("a report run has no text of its own"). So there is nothing
 * for the Fire-now UI to wait for, and waiting would only disable the composer
 * for minutes on a chat that is perfectly usable.
 */
export function deliversResultOutOfBand(automation: Automation): boolean {
  return automation.capability === 'insights';
}

/** Free-text body of an automation, read the way the server's projection reads it. */
export function automationBody(automation: Automation): string {
  return automationContent(automation.payload).text;
}

/**
 * The content kind and text, mirroring cti-conversation-service's `automationContent`: the first of
 * `message.text`, `task.prompt` and `checkin.question` whose object exists, otherwise a legacy
 * top-level `question` or `prompt`. A missing or non-string text reads as an empty string.
 */
function automationContent(
  payload: Record<string, unknown>,
): Pick<AutomationDetailProjection['content'], 'kind' | 'text'> {
  const message = plainRecord(payload.message);
  if (message) return { kind: 'message', text: stringOrEmpty(message.text) };
  const task = plainRecord(payload.task);
  if (task) return { kind: 'task', text: stringOrEmpty(task.prompt) };
  const checkin = plainRecord(payload.checkin);
  if (checkin)
    return { kind: 'checkin', text: stringOrEmpty(checkin.question) };
  const text =
    optionalText(payload.question) ?? optionalText(payload.prompt) ?? '';
  return { kind: 'legacy', text };
}

/** The value when it is a string, otherwise an empty string. */
function stringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Ids of the skills attached to an automation's payload: the top-level `skill_ids` first, then
 * `task.skill_ids`, as cti-conversation-service's `skillIdsOf` reads them. Filtered to well-formed
 * strings — mirrors the authoring-time validation in `AutomationsController#assertSkillsInvocable`.
 */
function extractSkillIds(payload: Record<string, unknown>): string[] {
  const ids = payload.skill_ids ?? plainRecord(payload.task)?.skill_ids;
  return Array.isArray(ids)
    ? ids.filter(
        (entry): entry is string =>
          typeof entry === 'string' && entry.trim().length > 0,
      )
    : [];
}

/**
 * The payload to send when the skills changed: the whole stored payload, copied, with the new ids
 * written where the service reads them. cti-conversation-service reads `payload.skill_ids` first and
 * falls back to `payload.task.skill_ids`, so the ids go under `task` only when the payload has a task
 * and no top-level list that would shadow it. An emptied list is still written as `[]`, so the server
 * is told the skills are gone.
 */
export function payloadWithSkillIds(
  payload: Record<string, unknown>,
  skillIds: readonly string[],
): Record<string, unknown> {
  const task = plainRecord(payload.task);
  const hasTopLevelIds =
    payload.skill_ids !== undefined && payload.skill_ids !== null;
  if (task && !hasTopLevelIds) {
    return { ...payload, task: { ...task, skill_ids: [...skillIds] } };
  }
  return { ...payload, skill_ids: [...skillIds] };
}

/** The user's own words for a custom schedule, read off a v1 payload; blank or missing is null. */
export function extractScheduleText(
  payload: Record<string, unknown>,
): string | null {
  return normalizeScheduleText(payload.schedule_text);
}

/**
 * Human label for a scheduler capability id. Falls back to the raw id so an
 * unknown capability still renders (best-effort, mirrors the prompt snippet).
 */
const CAPABILITY_LABELS: Record<string, string> = {
  insights: 'Insights',
  'agent-chat': 'Agent chat',
  'secretary-agent-message': 'Secretary agent message',
  'secretary-checkin': 'Secretary check-in',
  'secretary-message': 'Secretary message',
  'secretary-reminder': 'Reminder',
  'secretary-ping': 'Secretary ping',
};

export function capabilityLabel(capability: string): string {
  return CAPABILITY_LABELS[capability] ?? capability;
}

/** First non-empty line of the prompt, used for the row subtitle. */
export function firstLine(prompt: string | undefined): string {
  if (!prompt) return '';
  const line = prompt.split('\n').find((l) => l.trim().length > 0);
  return (line ?? '').trim();
}

/**
 * Preserve the scheduler's pause reason. For older responses without it, derive a spent
 * budget only for stopped rows so completed one-shots retain their label in fallback mode.
 */
function legacyPauseReason(automation: Automation): string | null {
  if (automation.pauseReason != null) return automation.pauseReason;
  const limitConsumed =
    automation.maxFires != null &&
    (automation.fireCount ?? 0) >= automation.maxFires;
  const stopped = automation.pausedAt != null || !automation.enabled;
  return limitConsumed && stopped ? 'max_fires' : null;
}

function projectLegacySummary(
  automation: Automation,
): AutomationSummaryProjection {
  const recipient = legacyRecipient(automation);
  return {
    id: automation.id,
    name: automation.name ?? null,
    capability: automation.capability,
    schedule: automation.schedule,
    enabled: automation.enabled,
    pausedAt: automation.pausedAt ?? null,
    nextFireAt: automation.nextFireAt ?? null,
    pauseReason: legacyPauseReason(automation),
    maxFires: automation.maxFires ?? null,
    fireCount: automation.fireCount ?? 0,
    latestExecution: null,
    skillIds: extractSkillIds(automation.payload),
    scheduleText: extractScheduleText(automation.payload),
    plan: null,
    parent: null,
    depth: 0,
    skip: null,
    runOnHolidays: automation.payload.run_on_holidays === true,
    pendingDeferral: null,
    visibility: 'private',
    canManage: true,
    participantIds: [],
    conversationId: legacyConversationId(automation),
    messageRoute: parseMessageRoute(automation.payload.message_route),
    recipientDisplayName: recipient?.displayName ?? null,
    preview: compactPreview(automationBody(automation)),
  };
}

/** The automation's dedicated-chat id off the v1 `target`, or `null` when it has none. */
function legacyConversationId(automation: Automation): string | null {
  const target = automation.target;
  return target?.dedicated === true &&
    typeof target.conversationId === 'string' &&
    target.conversationId.length > 0
    ? target.conversationId
    : null;
}

function projectLegacyDetail(
  automation: Automation,
): AutomationDetailProjection {
  const summary = projectLegacySummary(automation);
  const sourceRequest = legacySourceRequest(automation.payload);
  return {
    id: summary.id,
    name: summary.name,
    capability: summary.capability,
    schedule: summary.schedule,
    enabled: summary.enabled,
    pausedAt: summary.pausedAt,
    nextFireAt: summary.nextFireAt,
    pauseReason: summary.pauseReason,
    maxFires: summary.maxFires,
    fireCount: summary.fireCount,
    latestExecution: summary.latestExecution,
    skillIds: summary.skillIds,
    scheduleText: summary.scheduleText,
    plan: summary.plan,
    parent: summary.parent,
    depth: summary.depth,
    skip: summary.skip,
    runOnHolidays: summary.runOnHolidays,
    pendingDeferral: summary.pendingDeferral,
    visibility: summary.visibility,
    canManage: summary.canManage,
    participantIds: summary.participantIds,
    conversationId: summary.conversationId,
    messageRoute: summary.messageRoute,
    recipient: legacyRecipient(automation),
    content: {
      ...automationContent(automation.payload),
      ...(sourceRequest ? { sourceRequest } : {}),
    },
    owner: null,
  };
}

function legacyRecipient(
  automation: Automation,
): { displayName: string; email: string | null } | null {
  const target = automation.target as Record<string, unknown> | undefined;
  const canonical = plainRecord(target?.recipient);
  const displayName = optionalText(canonical?.display_name);
  const email = optionalText(canonical?.email) ?? null;
  if (displayName) return { displayName, email };
  const legacy = optionalText(automation.payload.recipient);
  if (!legacy) return null;
  return { displayName: legacy, email: legacy.includes('@') ? legacy : null };
}

function legacySourceRequest(
  payload: Record<string, unknown>,
): string | undefined {
  return optionalText(plainRecord(payload.audit)?.source_request);
}

function compactPreview(value: string): string {
  const compact = value.replace(/\s+/g, ' ').trim();
  return compact.length <= 160 ? compact : `${compact.slice(0, 159)}…`;
}

function plainRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function optionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Keep route display compatible with both owner projections and legacy payloads. */
function parseMessageRoute(value: unknown): AutomationMessageRoute | undefined {
  const route = plainRecord(value);
  if (route?.source === 'skill') return { source: 'skill' };
  if (route?.source !== 'user') return undefined;
  if (route.provider === 'teams') {
    const connectionId = optionalText(route.connection_id);
    if (!connectionId) return undefined;
    return {
      source: 'user',
      provider: 'teams',
      connection_id: connectionId,
      team_id: optionalText(route.team_id),
      channel_id: optionalText(route.channel_id),
      chat_id: optionalText(route.chat_id),
    };
  }
  const recipient = plainRecord(route.recipient);
  if (route.provider !== 'secretary' || !recipient) return undefined;
  const aadId = optionalText(recipient.aad_id);
  const email = optionalText(recipient.email);
  const name = optionalText(recipient.name);
  if (!aadId && !email && !name) return undefined;
  return {
    source: 'user',
    provider: 'secretary',
    recipient: { aad_id: aadId, email, name },
  };
}

/** Show the saved override or explain that the current skill supplies the route. */
export function automationMessageRouteLabel(
  route: AutomationMessageRoute | undefined,
): string | undefined {
  if (!route) return undefined;
  if (route.source === 'skill') return 'Current skill (updates each run)';
  if (route.provider === 'secretary') {
    return `Secretary · ${route.recipient.name ?? route.recipient.email ?? route.recipient.aad_id}`;
  }
  return [
    `Teams connection ${route.connection_id}`,
    route.team_id && `team ${route.team_id}`,
    route.channel_id && `channel ${route.channel_id}`,
    route.chat_id && `chat ${route.chat_id}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Conversation IDs of the caller's own dedicated automation chats (`target.dedicated`). Only the
 * caller's own — a shared automation's dedicated chat is added on top of this by
 * `useDedicatedAutomationConversationIds` below, from a separate, workspace-scoped read. NOTE:
 * correct only while automations fit the fetched page; past ~50 automations unfetched dedicated
 * chats leak into the normal list (documented v1 limit — the clean fix is a server-side
 * conversation marker).
 */
export function dedicatedConversationIds(
  automations: ReadonlyArray<Automation>,
): Set<string> {
  const ids = new Set<string>();
  for (const a of automations) {
    const t = a.target;
    if (
      t?.dedicated === true &&
      typeof t.conversationId === 'string' &&
      t.conversationId.length > 0
    ) {
      ids.add(t.conversationId);
    }
  }
  return ids;
}

/**
 * Result of {@link useDedicatedAutomationConversationIds}: the dedicated-chat ids subtracted so far,
 * and whether both underlying reads have settled — so a caller that renders a list from `ids` can
 * hold a loading state until a false negative (a shared dedicated chat not yet subtracted) can no
 * longer show.
 */
export interface DedicatedAutomationConversationIds {
  ids: Set<string>;
  /** True once the own-automations read and the selected workspace's summaries read have both
   *  settled (success or error); a disabled summaries read (no selected workspace) counts as settled. */
  isReady: boolean;
}

/**
 * Dedicated automation chats to keep out of the normal chat list: the caller's own (from the
 * unscoped `['automations']` query) plus the shared summaries of the selected workspace that the
 * caller cannot manage (from `['automation-summaries', ws]`) — a shared automation's chat must be
 * subtracted too, or it would show twice, once under Scheduled and once as a bare chat. Reads
 * `selectedWorkspaceId` itself so both callers (sidebar, command palette) stay unchanged. `isReady`
 * lets a caller hold its loading state until both reads have settled, so it never paints a shared
 * dedicated chat before it can be subtracted. NOTE: shares the ~50-automation page limit on both
 * reads (documented v1 limit).
 */
export function useDedicatedAutomationConversationIds(): DedicatedAutomationConversationIds {
  const { data, status: automationsStatus } = useQuery(
    automationsQueryOptions(),
  );
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const { data: sharedSummaries, status: summariesStatus } = useQuery(
    automationSummariesQueryOptions(selectedWorkspaceId),
  );
  const isReady =
    automationsStatus !== 'pending' &&
    (selectedWorkspaceId === null || summariesStatus !== 'pending');
  const ids = useMemo(() => {
    const ids = dedicatedConversationIds(data ?? []);
    for (const summary of sharedSummaries ?? []) {
      if (!summary.canManage && summary.conversationId !== null) {
        ids.add(summary.conversationId);
      }
    }
    return ids;
  }, [data, sharedSummaries]);
  return { ids, isReady };
}

/**
 * The automation whose dedicated chat is `conversationId`, or undefined. Reactive (subscribes to the
 * cached `['automations']` query so it stays in sync), mirroring `useDedicatedAutomationConversationIds`.
 * Used by the chat view to show a Fire-now affordance only on an automation's dedicated chat. Only
 * ever matches the caller's OWN automations — the v1 list underneath holds nothing shared into the
 * caller's workspace — see `useSharedAutomationForConversation` for that case. NOTE: shares the
 * ~50-automation page limit — an automation past the fetched page won't be matched (v1).
 */
export function useAutomationForConversation(
  conversationId: string | null | undefined,
): Automation | undefined {
  const { data } = useQuery({
    ...automationsQueryOptions(),
    enabled: !!conversationId,
  });
  return useMemo(() => {
    if (!conversationId) return undefined;
    return (data ?? []).find(
      (a) =>
        a.target?.dedicated === true &&
        a.target.conversationId === conversationId,
    );
  }, [data, conversationId]);
}

/**
 * The shared (not owned) automation whose dedicated chat is `conversationId`, scoped to the
 * selected workspace — the read `useAutomationForConversation` uses never carries an automation the
 * caller does not own, so a reader's dedicated chat needs this separate, workspace-scoped summaries
 * read instead. Used by the chat header to show a `Scheduled automation` pill in place of the
 * author-only Fire-now and Share affordances.
 */
export function useSharedAutomationForConversation(
  conversationId: string | null | undefined,
): AutomationSummaryProjection | undefined {
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const { data } = useQuery({
    ...automationSummariesQueryOptions(selectedWorkspaceId),
    enabled: !!conversationId && selectedWorkspaceId !== null,
  });
  return useMemo(() => {
    if (!conversationId) return undefined;
    return (data ?? []).find(
      (summary) =>
        !summary.canManage && summary.conversationId === conversationId,
    );
  }, [data, conversationId]);
}
